import { apiClient } from './apiClient';

/**
 * AI suggestion service for the Brand Bible page.
 *
 * Calls POST /brand-bible/ai-suggest (backend route to be added) with a prompt and
 * returns a list of strings. Falls back to a tiny mocked response if the endpoint
 * returns 404 — keeps the UI usable while the backend route is wired up.
 */
export interface AISuggestRequest {
  prompt: string;
  /** Stable Brand Bible section id, used by the deterministic backend fallback. */
  section_id?: string;
  /** What to expect back: short chip strings (8 max) or longer text rewrites (3 max). */
  kind: 'chips' | 'text';
  /** Items already chosen — passed through to the prompt to avoid re-suggesting. */
  existing?: string[];
  /** Items the user dismissed — also avoided. */
  dismissed?: string[];
}

export interface AISuggestResponse {
  items: string[];
  /** Optional source provenance shown to the user (e.g. "from your homepage"). */
  source?: string;
}

const MOCK_BY_HASH: Record<string, string[]> = {
  service: ['Drain cleaning', 'Sewer line', 'Hydro jetting', 'Sump pump install', 'Backflow testing'],
  guardrails: ['cheap', 'guys', 'best in town', '#1', 'hack'],
  audience: ['Hidden fees', 'Slow response', 'No follow-up', 'Poor cleanup', 'Hard upsell'],
  voice: [
    'Friendly, expert, direct. Active voice. No slang. Short sentences when explaining trade work.',
    'Calm, knowledgeable, transparent. Uses concrete numbers over adjectives. Avoids urgency-pressure language.',
  ],
  visual: [
    'Real technicians in branded uniforms, natural daylight, no stock photography. Focus on the customer interaction.',
    'Clean before/after pairs. Wide shots of completed work. Authentic, slightly imperfect to feel real.',
  ],
  proof: [
    'Add only verified credentials, partnerships, awards, reviews, or certifications found on the website.',
    'Use concrete case-study or testimonial details only when the source page explicitly supports them.',
  ],
  essentials: [
    'Family-owned local provider serving the area since [year]. We specialize in [top services] and stand behind every job with a satisfaction guarantee.',
    '[Trade] experts trusted by homeowners across [region]. Transparent pricing, same-day response, and zero hidden fees.',
  ],
};

const pickMock = (sectionId: string, kind: 'chips' | 'text'): string[] => {
  const base = MOCK_BY_HASH[sectionId];
  if (base) return base;
  return kind === 'chips' ? ['Suggestion A', 'Suggestion B', 'Suggestion C'] : ['Sample rewrite suggestion.'];
};

export const aiSuggestService = {
  async suggest(req: AISuggestRequest, sectionId: string): Promise<AISuggestResponse> {
    try {
      const { data } = await apiClient.post<AISuggestResponse>('/brand-bible/ai-suggest', {
        ...req,
        section_id: sectionId,
      });
      return data;
    } catch (err: any) {
      if (err.response?.status === 404 || err.code === 'ERR_NETWORK') {
        // Backend route not deployed yet — return seeded mock so the UI stays usable.
        return { items: pickMock(sectionId, req.kind), source: 'demo data (backend pending)' };
      }
      throw err;
    }
  },
};
